import React, { useEffect, useMemo, useState } from 'react';
import { Spinner } from '@nextui-org/react';
import { useTranslation } from 'react-i18next';
import { CtyShowToast, checkType } from '../toast/CtyToast';
import {
    useSorApi,
    type SorQueryResponse,
    type SorRecord,
} from '../hooks/useSorApi';
import { conversationHistoryTimezone } from '../utils/dateutils';
import { formatSectionTitle, type ActionOutputV2Payload } from './actionOutputV2Utils';
import {
    renderV2TopLevelBlocks,
    useClaimScoreActionOutputV2TopLevelItems,
} from './ClaimScoreActionOutputV2Renderer';

type InspectionItemData = {
    evidence?: unknown;
};

type SorDetails = {
    definition: string;
    inspectionId: string;
};

const PAGE_LIMIT = 100;

const isRecord = (value: unknown): value is Record<string, unknown> =>
    typeof value === 'object' && value !== null && !Array.isArray(value);

/** Message chip still reads `meta_data.message` from the usage action output. */
export const readVirtualInspectionMessage = (actionOutput: unknown, v2Payload: unknown): unknown => {
    if (isRecord(v2Payload) && isRecord(v2Payload.meta_data)) {
        const fromV2 = v2Payload.meta_data.message;
        if (fromV2 != null && fromV2 !== '') return fromV2;
    }
    if (isRecord(actionOutput) && isRecord(actionOutput.meta_data)) {
        return actionOutput.meta_data.message;
    }
    return undefined;
};

/** SOR item query runs only when `metadata.sor.inspection_id` is a non-empty string. */
export const readVirtualInspectionSorDetails = (usage: unknown): SorDetails | null => {
    if (!isRecord(usage) || !isRecord(usage.metadata) || !isRecord(usage.metadata.sor)) return null;
    const sor = usage.metadata.sor;
    const definition = typeof sor.insp_item_definition === 'string' ? sor.insp_item_definition.trim() : '';
    const inspectionId = typeof sor.inspection_id === 'string' ? sor.inspection_id.trim() : '';
    if (!definition || !inspectionId) return null;
    return { definition, inspectionId };
};

const hasEvidenceFile = (entry: Record<string, unknown>): boolean =>
    typeof entry.id === 'string' && entry.id.trim().length > 0
    && typeof entry.fileName === 'string' && entry.fileName.trim().length > 0;

const evidenceWithFiles = (evidence: unknown): Record<string, unknown>[] => {
    if (!Array.isArray(evidence)) return [];
    return evidence.filter((entry): entry is Record<string, unknown> => isRecord(entry) && hasEvidenceFile(entry));
};

const toAttachmentItems = (files: Record<string, unknown>[]): Record<string, unknown>[] =>
    files.map((entry) => {
        const caption = typeof entry.caption === 'string' ? entry.caption.trim() : '';
        return {
            source_id: entry.id,
            file_name: entry.fileName,
            ...(caption ? { description: caption } : {}),
        };
    });

/** SOR sends `MM-DD-YYYY HH:mm:ss`. Header timestamps use `YYYY-MM-DD HH:mm:ss`. */
const MDY_TIMESTAMP = /^(\d{2})-(\d{2})-(\d{4}) (\d{2}:\d{2}:\d{2})$/;

const formatShownTimestamp = (value: unknown): string | undefined => {
    if (typeof value !== 'string') return undefined;
    const trimmed = value.trim();
    if (!trimmed) return undefined;
    const match = MDY_TIMESTAMP.exec(trimmed);
    const month = match ? Number(match[1]) : 0;
    const day = match ? Number(match[2]) : 0;
    const normalized = match && month >= 1 && month <= 12 && day >= 1 && day <= 31
        ? `${match[3]}-${match[1]}-${match[2]} ${match[4]}`
        : trimmed;
    return conversationHistoryTimezone(normalized) || trimmed;
};

const withFormattedTimestamp = (location: Record<string, unknown>): Record<string, unknown> => {
    const formatted = formatShownTimestamp(location.timestamp);
    if (!formatted || formatted === location.timestamp) return location;
    return { ...location, timestamp: formatted };
};

const firstEvidenceLocation = (files: Record<string, unknown>[]): Record<string, unknown> | null => {
    for (const entry of files) {
        if (isRecord(entry.location)) return withFormattedTimestamp(entry.location);
    }
    return null;
};

/** One `items` array, so the existing renderer titles it Items, then Items #1, Items #2. */
const buildVirtualInspectionPayload = (
    items: SorRecord<InspectionItemData>[]
): ActionOutputV2Payload => {
    const rows: Record<string, unknown>[] = [];
    items.forEach((item, index) => {
        const files = evidenceWithFiles(item.data?.evidence);
        if (files.length === 0) return;
        const display = typeof item.display === 'string' && item.display.trim()
            ? item.display
            : `Item ${index + 1}`;
        const fields: Record<string, unknown> = {
            name: formatSectionTitle(display),
            '@attachment': toAttachmentItems(files),
        };
        const location = firstEvidenceLocation(files);
        if (location) fields.location = location;
        rows.push(fields);
    });
    return {
        version: 2,
        explanation: rows.length > 0 ? { items: rows } : {},
        order: rows.length > 0 ? ['items'] : undefined,
    };
};

type VirtualInspectionSorDetailsProps = {
    definition: string;
    inspectionId: string;
    agentName: string;
    searchQuery: string;
    activeFilter: 'matches' | 'partial' | 'mismatches' | null;
    stopGatePath: string[] | null;
    nestedSelectedKeys: Set<string>;
    onNestedSelectedKeysChange: (keys: Set<string>) => void;
    timestamp?: string;
    setSourceJsonModal: (state: { title: string; data: unknown } | null) => void;
    selectedKeys: Set<string>;
    onSelectionChange: (keys: 'all' | Set<React.Key>) => void;
};

/**
 * Virtual Inspection item rows. Message and review state stay on the usage record.
 * Name and attachments come from SOR `records/query`, using the work-queue SOR token.
 */
export const VirtualInspectionSorDetails: React.FC<VirtualInspectionSorDetailsProps> = ({
    definition,
    inspectionId,
    agentName,
    searchQuery,
    activeFilter,
    stopGatePath,
    nestedSelectedKeys,
    onNestedSelectedKeysChange,
    timestamp,
    setSourceJsonModal,
    selectedKeys,
    onSelectionChange,
}) => {
    const { t } = useTranslation();
    const { queryRecords } = useSorApi();
    const [items, setItems] = useState<SorRecord<InspectionItemData>[] | null>(null);
    const [isLoading, setIsLoading] = useState(false);

    useEffect(() => {
        const controller = new AbortController();
        let cancelled = false;
        setIsLoading(true);
        setItems(null);

        const load = async () => {
            const collected: SorRecord<InspectionItemData>[] = [];
            let offset = 0;
            for (;;) {
                const page = await queryRecords<SorQueryResponse<InspectionItemData>>({
                    definition,
                    filter: {
                        field: 'data.inspectionId',
                        op: 'eq',
                        value: inspectionId,
                    },
                    page: { limit: PAGE_LIMIT, offset },
                }, controller.signal);
                const pageItems = page?.items ?? [];
                collected.push(...pageItems);
                const total = typeof page?.total === 'number' ? page.total : collected.length;
                if (pageItems.length === 0 || collected.length >= total) break;
                offset += pageItems.length;
            }
            return collected;
        };

        load()
            .then((collected) => {
                if (!cancelled) setItems(collected);
            })
            .catch((error: unknown) => {
                if (cancelled || controller.signal.aborted) return;
                const message = error instanceof Error && error.message
                    ? error.message
                    : t('Failed to load inspection items');
                CtyShowToast(checkType.error, message);
                setItems([]);
            })
            .finally(() => {
                if (!cancelled) setIsLoading(false);
            });

        return () => {
            cancelled = true;
            controller.abort();
        };
    }, [definition, inspectionId, queryRecords, t]);

    const payload = useMemo(
        () => (items ? buildVirtualInspectionPayload(items) : null),
        [items]
    );

    const { blocks } = useClaimScoreActionOutputV2TopLevelItems({
        data: payload ?? { version: 2, explanation: {} },
        enabled: Boolean(payload),
        searchQuery,
        activeFilter,
        stopGatePath,
        nestedSelectedKeys,
        onNestedSelectedKeysChange,
        timestamp,
        setSourceJsonModal,
        agentName,
    });

    if (isLoading || items == null) {
        return (
            <div className="flex items-center justify-center p-6">
                <Spinner size="sm" aria-label={t('Loading')} />
            </div>
        );
    }
    return renderV2TopLevelBlocks(blocks, { selectedKeys, onSelectionChange });
};
